import { MindfulMinute } from '~models';
import { mindfulMinute } from '~api';
import { Handler } from '~services';
import router from '~router';
import { getField, updateField } from 'vuex-map-fields';
import i18n from '../i18n';

const SET_SESSION = 'SET_SESSION';
const SET_SESSION_IMAGE = 'SET_SESSION_IMAGE';
const SET_SESSION_VIDEO = 'SET_SESSION_VIDEO';
const RESET_SESSION = 'RESET_SESSION';
const SET_SESSIONS = 'SET_SESSIONS';
const LOADING = 'LOADING';

const state = {
    session: { ...new MindfulMinute() },
    isLoading: false,
    sessions: []
};

const actions = {
    toggleLoading({ commit }) {
        commit(LOADING);
    },

    /**
     * Reset session state.
     * @param {function} commit
     */
    resetSession({ commit }) {
        commit(RESET_SESSION);
    },

    async getBannerSessionMinutes({ commit }) {
        const resp = await mindfulMinute.featured({
            page: 1
        });

        if (resp.ok && resp.data.length) {
            let minute = resp.data[resp.data.length - 1];
            if (minute.videoName) {
                minute = resp.data[resp.data.length - 2]
                    ? resp.data[resp.data.length - 2]
                    : resp.data[0];
            }
            commit(SET_SESSION, minute);
        }
    },

    /**
     * Reset session state and redirect to session list.
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_SESSION);
        router.push({ name: 'dashboard.mindfulMinutes' });
    },

    /**
     * Upload image for session
     * @param {function} commit
     */
    async uploadImage({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('sessions.edit.successMessageAvatar');
        const resp = await mindfulMinute.uploadImage(data);

        if (resp.ok)
            commit(SET_SESSION_IMAGE, { name: data.name, url: resp.data });

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Upload image for session
     * @param {function} commit
     */
    async uploadVideo({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('managers.edit.successMessageAvatar');
        const resp = await mindfulMinute.uploadVideo(data);

        if (resp.ok)
            commit(SET_SESSION_VIDEO, { name: data.name, url: resp.data });

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Create session account.
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('sessions.create.successMessage');
        const redirectLink = 'dashboard.mindfulMinutes';

        let {
            title,
            poster,
            poster_name,
            video,
            videoName,
            description,
            feedLabel,
            sendPush
        } = state.session;

        commit(LOADING);

        const params = {
            title,
            description,
            poster,
            video,
            videoName,
            poster_name,
            isLocked: false,
            mode: 'minute',
            feedLabel,
            sendPush
        };

        // API Request to Create Session
        const resp = await mindfulMinute.create(params);

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        commit(LOADING);
    },

    /**
     * Get Session.
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        // API Request get session by id
        let resp = await mindfulMinute.read(id);

        if (resp.ok) {
            const { data } = resp;
            commit(SET_SESSION, data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }
    },

    /**
     * Update Session.
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('sessions.edit.successMessage');
        const redirectLink = 'dashboard.mindfulMinutes';
        let {
            title,
            poster,
            video,
            videoName,
            poster_name,
            description,
            feedLabel,
            sendPush
        } = state.session;

        commit(LOADING);

        const params = {
            title,
            description,
            poster,
            poster_name,
            video,
            videoName,
            feedLabel,
            isLocked: false,
            sendPush
        };

        // API Request to Update Session
        const resp = await mindfulMinute.update(state.session.id, params);

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        commit(LOADING);
    },

    /**
     * Get Sessions by Mindful minutes theme.
     * @param {function} commit
     */
    async list({ commit }) {
        // API Request to get session list with type recorded
        const resp = await mindfulMinute.list();

        if (resp.ok) {
            const { data } = resp;

            commit(SET_SESSIONS, data.items);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }
    },

    /**
     * Delete Session.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async deleteSession({ commit, dispatch, state }) {
        const successMessage = i18n.t('sessions.edit.delete.successMessage');
        const redirectLink = 'dashboard.mindfulMinutes';

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.deleteSession'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // API Request on delete session
        const resp = await mindfulMinute.delete(state.session.id);

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        commit(LOADING);
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_SESSION](state, data) {
        state.session = data;

        state.isLoading = false;
    },
    [RESET_SESSION](state) {
        state.session = { ...new MindfulMinute() };
        state.isLoading = false;
    },
    [SET_SESSIONS](state, data) {
        state.sessions = data;
    },

    [SET_SESSION_IMAGE](state, data) {
        state.session.photo = data.name;
        state.session.photoName = data.url;
    },
    [SET_SESSION_VIDEO](state, data) {
        state.video = data;
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    sessions: state => state.sessions,
    session: state => state.session,
    bannerSessionMinutes: state => state.session
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
